import React from "react";
import { Utensils, Coffee, Gift } from "lucide-react";
import { Card, Input, Toggle, Textarea, PillGroup, TagGroup } from "./FormComponents";

export const Step10 = ({ data, updateData, isLight, showValidation }) => {
  const handleChange = (field, value) => {
    updateData("kitchenDining", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("kitchenDining", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const applianceOptions = ["Refrigerator", "Freezer", "Stovetop", "Oven", "Microwave", "Toaster", "Kettle", "Blender", "Dishwasher", "Other"];
  const cookwareOptions = ["Cookware", "Utensils", "Other"];
  const diningOptions = ["Cutlery", "Tableware", "Glassware", "Other"];
  const coffeeOptions = ["Filter", "Espresso Machine", "French Press", "Other"];
  const complimentaryOptions = ["Coffee", "Tea", "Sugar", "Salt", "Pepper", "Olive oil", "Welcome snacks", "Bottled water", "Other"];
  const supplyOptions = ["Dish Soap", "Sponge", "Trash Bags", "Paper Towels", "Other"];

  return (
    <div className="flex flex-col gap-8">
      <Card title="Kitchen" icon={Utensils} subtitle="Tell us about your kitchen and what guests can use." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <PillGroup 
            label="What type of kitchen is available?" required
            options={["Full Kitchen", "Kitchenette", "No Kitchen"]} 
            value={data.type} isLight={isLight}
            showValidation={showValidation}
            onChange={(val) => handleChange("type", val)}
          />

          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
              <TagGroup 
                label="Appliances" options={applianceOptions}
                value={data.appliances} isLight={isLight}
                showValidation={showValidation}
                onChange={(val) => {
                  const defaultOvenInstructions = "To use it, turn on the oven, choose the desired cooking mode, and set the temperature. Wait a few minutes for the oven to preheat before placing food inside. After cooking, please make sure the oven is completely turned off. For safety reasons, do not leave the oven unattended while it is in use.";
                  const updates = { 
                    appliances: val,
                    oven: { ...data.oven, available: val.includes("Oven"), instructions: val.includes("Oven") ? (data.oven.instructions || defaultOvenInstructions) : "" },
                    dishwasher: { ...data.dishwasher, available: val.includes("Dishwasher"), instructions: val.includes("Dishwasher") ? data.dishwasher.instructions : "" }
                  };
                  if (!val.includes("Other")) updates.otherAppliances = "";
                  updateData("kitchenDining", { ...data, ...updates });
                }}
              />
              {data.appliances.includes("Other") && (
                <Input 
                  label="Specify other appliances" placeholder="e.g., Air Fryer, Wine Cooler..." required
                  value={data.otherAppliances} isLight={isLight}
                  showValidation={showValidation}
                  onChange={(e) => handleChange("otherAppliances", e.target.value)}
                />
              )}
            </div>

            {(data.appliances.includes("Oven") || data.appliances.includes("Dishwasher")) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {data.appliances.includes("Oven") && (
                  <Textarea 
                    label="How do guests use the oven?" 
                    placeholder="Turn the left knob to the desired temperature..." required
                    value={data.oven.instructions} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("oven", "instructions", e.target.value)}
                  />
                )}
                {data.appliances.includes("Dishwasher") && (
                  <Textarea 
                    label="How do guests use the dishwasher?" 
                    placeholder="Use the tablets under the sink. Select the 30min cycle." required
                    value={data.dishwasher.instructions} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("dishwasher", "instructions", e.target.value)}
                  />
                )}
              </div>
            )}

            <div className="flex flex-col gap-6">
              <TagGroup label="Cookware" options={cookwareOptions} value={data.cookware} isLight={isLight} showValidation={showValidation} 
                onChange={(val) => {
                  const updates = { cookware: val };
                  if (!val.includes("Other")) updates.otherCookware = "";
                  updateData("kitchenDining", { ...data, ...updates });
                }} 
              />
              {data.cookware.includes("Other") && (
                <Input 
                  label="Specify other cookware" placeholder="e.g., Wok, Paella pan..." required
                  value={data.otherCookware} isLight={isLight}
                  showValidation={showValidation}
                  onChange={(e) => handleChange("otherCookware", e.target.value)}
                />
              )}
            </div>

            <div className="flex flex-col gap-6">
              <TagGroup label="Dining Essentials" options={diningOptions} value={data.diningEssentials} isLight={isLight} showValidation={showValidation} 
                onChange={(val) => {
                  const updates = { diningEssentials: val };
                  if (!val.includes("Other")) updates.otherDiningEssentials = "";
                  updateData("kitchenDining", { ...data, ...updates });
                }} 
              />
              {data.diningEssentials.includes("Other") && (
                <Input 
                  label="Specify other dining essentials" placeholder="e.g., Wine glasses, Place mats..." required
                  value={data.otherDiningEssentials} isLight={isLight}
                  showValidation={showValidation}
                  onChange={(e) => handleChange("otherDiningEssentials", e.target.value)}
                />
              )}
            </div>

            <div className="flex flex-col gap-6">
              <TagGroup label="Coffee setup" options={coffeeOptions} value={data.coffeeSetup} isLight={isLight} showValidation={showValidation} 
                onChange={(val) => {
                  const updates = { coffeeSetup: val };
                  if (!val.includes("Other")) updates.otherCoffeeSetup = "";
                  updateData("kitchenDining", { ...data, ...updates });
                }} 
              />
              {data.coffeeSetup.includes("Other") && (
                <Input 
                  label="Specify other coffee setup" placeholder="e.g., Nespresso compatible pods..." required
                  value={data.otherCoffeeSetup} isLight={isLight}
                  showValidation={showValidation}
                  onChange={(e) => handleChange("otherCoffeeSetup", e.target.value)}
                />
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card title="Kitchen Essentials" icon={Gift} subtitle="Everyday kitchen items available during the stay." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <TagGroup label="Complimentary Items" options={complimentaryOptions} value={data.complimentaryItems} isLight={isLight} showValidation={showValidation} 
              onChange={(val) => {
                const updates = { complimentaryItems: val };
                if (!val.includes("Other")) updates.otherComplimentaryItems = "";
                updateData("kitchenDining", { ...data, ...updates });
              }} 
            />
            {data.complimentaryItems.includes("Other") && (
              <Input 
                label="Specify other complimentary items" placeholder="e.g., Honey, Fruit bowl..." required
                value={data.otherComplimentaryItems} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleChange("otherComplimentaryItems", e.target.value)}
              />
            )}
          </div>

          <div className="flex flex-col gap-6">
            <TagGroup label="Kitchen Supplies" options={supplyOptions} value={data.kitchenSupplies} isLight={isLight} showValidation={showValidation} 
              onChange={(val) => {
                const updates = { kitchenSupplies: val };
                if (!val.includes("Other")) updates.otherKitchenSupplies = "";
                updateData("kitchenDining", { ...data, ...updates });
              }} 
            />
            {data.kitchenSupplies.includes("Other") && (
              <Input 
                label="Specify other supplies" placeholder="e.g., Aluminum foil, Cling film..." required
                value={data.otherKitchenSupplies} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleChange("otherKitchenSupplies", e.target.value)}
              />
            )}
          </div>

          <Toggle 
            label="May some consumable items occasionally be unavailable?" 
            value={data.consumablesOccasionallyUnavailable} isLight={isLight}
            onChange={(val) => handleChange("consumablesOccasionallyUnavailable", val)}
          />
        </div>
      </Card>
    </div>
  );
};
